import queryString from 'query-string';
import { useCallback } from 'react';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useSearchGetters } from '#/modules/search/contexts/SearchContext';
import { searchGetPathSwitch } from '#/modules/search/routing/helpers/searchGetPathSwitch';

type Args = {
  isInSearchLandingPage: boolean;
};

export function useSearchPageActionsHelpers({ isInSearchLandingPage }: Args) {
  const { locale } = useI18n();

  const {
    getSearchQuery,
    getSearchFiltersByName,
    getSearchRadius,
    getSearchSort,
    getSearchPageIndex,
    getSearchFilterType,
  } = useSearchGetters();
  const navigate = useDeferredNavigate();

  const getDerivedSearchParamsFromState = useCallback(() => {
    const url = searchGetPathSwitch({
      locale,
      q: getSearchQuery(),
      filtersByName: getSearchFiltersByName(),
      radius: getSearchRadius(),
      sort: getSearchSort(),
      pageIndex: getSearchPageIndex(),
      // We only derive from state in search landing pages, which don't exist on the widget
      embedSearchWidgetId: null,
    });

    const analyticsSearchParams = queryString.parse(window.location.search);
    const newSearchParams = {
      ...analyticsSearchParams,
      ...queryString.parseUrl(url).query,
    };

    return queryString.stringify(newSearchParams);
  }, [
    locale,
    getSearchFiltersByName,
    getSearchPageIndex,
    getSearchQuery,
    getSearchRadius,
    getSearchSort,
  ]);

  /**
   * Set query params with only the given values
   */
  const resetQueryParams = useCallback(
    (
      values: Record<string, unknown>,
      locationState?: Record<string, unknown>,
    ) => {
      const pathname = isInSearchLandingPage
        ? searchGetPathSwitch({
            locale,
            q: getSearchQuery(),
            filtersByName: { type: getSearchFilterType() },
            embedSearchWidgetId: null, // cannot be landing page and widget at the same time
          }).split('?')[0]
        : window.location.pathname;

      void navigate(
        {
          pathname,
          search: queryString.stringify(values),
        },
        { state: locationState, replace: true },
      );
    },
    [
      getSearchFilterType,
      getSearchQuery,
      isInSearchLandingPage,
      locale,
      navigate,
    ],
  );

  /**
   * Update query params, carrying over previous values
   */
  const updateQueryParams = useCallback(
    (
      values: Record<
        string,
        string | string[] | Record<string, unknown> | number | null | undefined
      >,
      locationState?: Record<string, unknown>,
    ) => {
      const newQueryParams = { ...values };
      const previousQueryParams = queryString.parse(
        isInSearchLandingPage
          ? getDerivedSearchParamsFromState()
          : window.location.search,
      );

      // `actionDate` is different from the other filters, it is an object filter, so we need special handling for it
      const { actionDate } = newQueryParams;
      if (actionDate && typeof actionDate === 'object') {
        if ('startsLT' in actionDate && actionDate.startsLT)
          newQueryParams.startsLT = actionDate.startsLT as string;

        if ('endsGT' in actionDate && actionDate.endsGT)
          newQueryParams.endsGT = actionDate.endsGT as string;

        delete newQueryParams.actionDate;

        // Avoid actionDate url params getting re-added by the previous query params
        delete previousQueryParams.startsLT;
        delete previousQueryParams.endsGT;
      }

      // If the new value is set explicitly to `undefined`, don't bring over the previous query param
      Object.keys(values).forEach((key) => {
        const value = values[key];
        if (typeof value === 'undefined') delete previousQueryParams[key];
      });

      resetQueryParams(
        { ...previousQueryParams, ...newQueryParams },
        locationState,
      );
    },
    [getDerivedSearchParamsFromState, isInSearchLandingPage, resetQueryParams],
  );

  return { updateQueryParams, resetQueryParams };
}
